// React
import { useEffect, useMemo } from 'react';

// Redux
import { useDispatch, useSelector } from 'react-redux';

// Ant Design
import { Col, Row, Space, Typography } from 'antd';

// TanStack Query
import { useQueryClient } from '@tanstack/react-query';

// Common components
import {
  CustomNotification,
  dialogFunction,
  FormButton,
  FormSaveButton,
  StyledModal,
  WithLoading,
} from 'common/components';
import {
  RequiredActiveProductModal,
  RequiredActiveProductSection,
} from 'common/components/product-add/make-product-active/RequiredActiveProduct';
import {
  RequiredModuleName,
  RequiredPropertiesForm,
  RequiredPropertiesSection,
  RequiredPropertyItem,
  RequiredPropertyName,
} from 'common/components/product-add/make-product-active/RequiredPropertyElement';
import { RequiredPropertyInput } from 'common/components/product-add/make-product-active/RequiredPropertyInput';
import { AdditionalRequiredActiveProduct } from 'common/components/product-add/make-product-active/AdditionalRequiredActiveProduct';

// Common hooks
import {
  useRequiredActiveProductContext,
  useSetFormValuesAndErrors,
  useUpdateUnmatchedRequirement,
} from 'common/components/product-add/make-product-active/hooks';

// Common utils
import { mergeProductImagery } from 'common/components/product-add/components/utils/images';
import { additionalRequirementFieldsLowercase } from 'common/components/product-add/make-product-active/constants';
import {
  checkPassedRequirementProduct,
  filterInvalidValuesFormValues,
} from 'common/components/product-add/make-product-active/utils';

// Pages/Hooks
import {
  useMutateMakeProductActive,
  useValidateActiveProduct,
} from 'pages/home/ribbon/components/sections/product/components/make-product-active/hooks';
import { getErrorsForActiveProduct } from 'pages/home/ribbon/components/sections/product/components/make-product-active/utils';
import { mappingGridProperties as groupPropertiesByModuleName } from 'pages/home/ribbon/components/sections/customize-grid/utils';
import { useGetProductImagesByStatus } from 'hooks/useGetProductImagesByStatus';
import { useGetRequirementsForActiveProduct } from 'hooks/entityHooks';
import { productFullViewKeys, useGetProductFullView } from 'hooks';
import { useQueryMarketingAssets } from 'pages/product-full-view/queries';
import { lowercaseFirstChar } from 'hooks/useValidateSelfLoadedProduct';

// Redux actions and selectors
import * as productFullViewActions from 'pages/product-full-view/controllers/actions';
import * as productFullViewSelectors from 'pages/product-full-view/controllers/selectors';

// Utils
import { getPropertiesParams } from 'utils/minimumRequirementUtils';

const { Text, Paragraph } = Typography;

export const CreateProductRequiredFieldsModal = (props) => {
  const {
    visible,
    onCancel,
    newProductData,
    productImagery,
    setValidateProducts,
    validateProducts,
    setValidateErrors,
    reloadPage,
    setProductImagery,
  } = props;

  const productId = newProductData?.productId;
  const isPrivateLabel = newProductData?.isPrivateLabel;

  const {
    validateProducts: initialValidateProducts,
    data,
    dataUpdatedAt,
    handleRevalidateActiveProduct,
    refetchAndGetValidateProducts,
  } = useValidateActiveProduct({
    productIds: [newProductData?.productId],
    staleTime: 0,
  });

  const { initialProductImagery } = useGetProductImagesByStatus({
    status: newProductData?.status,
    productId,
    options: {
      enabled: !!newProductData?.status && !!productId,
    },
  });

  useEffect(() => {
    if (initialProductImagery) {
      setProductImagery((prevValue) =>
        mergeProductImagery(prevValue, initialProductImagery)
      );
    }
  }, [initialProductImagery, setProductImagery]);

  useEffect(() => {
    if (initialValidateProducts) {
      setValidateProducts(initialValidateProducts);
    }
  }, [initialValidateProducts, setValidateProducts]);

  useEffect(() => {
    if (data) {
      setValidateErrors({ ...data?.products?.[0]?.activeResult });
    }
  }, [data, dataUpdatedAt, setValidateErrors]);

  return (
    <>
      {isPrivateLabel ? (
        <RequiredActivePrivateProductModal
          visible={true}
          onCancel={onCancel}
          entityId={productId}
          entityType={'product'}
          validateProducts={validateProducts}
          onMakeActiveSuccessCallback={() => {
            reloadPage();
            onCancel();
            CustomNotification.success('Product activated successfully');
          }}
          onRefetchAndGetValidateProducts={refetchAndGetValidateProducts}
        >
          <RequiredActivePrivateProductSection
            entityId={productId}
            validateProducts={validateProducts}
            productImagery={productImagery}
            validationTimestamp={dataUpdatedAt}
            onRevalidateProduct={handleRevalidateActiveProduct}
          />
        </RequiredActivePrivateProductModal>
      ) : (
        <RequiredActiveProductModal
          visible={visible}
          onCancel={onCancel}
          entityId={productId}
          entityType={'product'}
          validateProducts={validateProducts}
          onMakeActiveSuccessCallback={reloadPage}
          onUpdateUnmatchedSuccessCallback={handleRevalidateActiveProduct}
        >
          <RequiredActiveProductSection
            entityId={productId}
            entityType='product'
            validateProducts={validateProducts}
            productImagery={productImagery}
            validationTimestamp={dataUpdatedAt}
            onRevalidateProduct={handleRevalidateActiveProduct}
          />
        </RequiredActiveProductModal>
      )}
    </>
  );
};
const RequiredActivePrivateProductModal = ({
  entityId,
  validateProducts,
  children,
  onCancel,
  onMakeActiveSuccessCallback = () => {},
  onRevalidateProduct = () => {},
  onRefetchAndGetValidateProducts,
  ...otherProps
}) => {
  const { requiredForm } = useRequiredActiveProductContext();

  const unmatchedRequirementMutation = useUpdateUnmatchedRequirement();
  const mutateMakeProductActive = useMutateMakeProductActive();

  const dispatch = useDispatch();

  const { productFull } = useGetProductFullView({
    productId: entityId,
  });

  const queryClient = useQueryClient();

  const productHierarchyInfo = useSelector(
    productFullViewSelectors.makeSelectCurrentHierarchy()
  );

  const activeRequiredFields =
    validateProducts?.[0]?.activeResult?.activeRequiredFields ?? [];

  const { invalidateMarketingAssets } = useQueryMarketingAssets({
    enabled: false,
  });

  /**
   * Collects form values from both product form and required form.
   */
  const collectFormValues = () => {
    const requiredFormValues = requiredForm?.getFieldsValue() ?? {};

    let formValues = {
      ...requiredFormValues,
    };

    // Remove invalid values
    formValues = filterInvalidValuesFormValues(formValues);

    // Normalize brickCode field
    formValues = {
      ...formValues,
      brickCode: formValues?.brickCode || formValues?.brick,
    };

    return formValues;
  };

  const filterRequiredFields = (formValues, mappingActiveRequiredFields) => {
    return mappingActiveRequiredFields.reduce((acc, field) => {
      if (field in formValues) {
        acc[field] = formValues[field];
      }
      return acc;
    }, {});
  };

  const buildPayload = (formValues, filteredFormValues, status) => {
    return {
      ...formValues,
      ...filteredFormValues,
      productId: formValues?.productId || formValues?.id || entityId,
      isVariableWeightsAndMeasures:
        formValues?.isVariableWeightsAndMeasures ??
        productFull?.isVariableWeightsAndMeasures ??
        false,
      packageLevel: formValues?.packageLevel,
      status: status ?? formValues?.status,
    };
  };

  /**
   * Updates unmatched requirement fields for the product.
   * This function collects form values, filters required fields, and submits them to update the product.
   */
  const handleUpdateUnmatchedRequirement = async ({ status } = {}) => {
    try {
      const mappingActiveRequiredFields = activeRequiredFields.map((field) =>
        lowercaseFirstChar(field)
      );

      const formValues = collectFormValues();
      const filteredRequiredValues = filterRequiredFields(
        formValues,
        mappingActiveRequiredFields
      );
      const payload = buildPayload(formValues, filteredRequiredValues, status);

      const requestParams = {
        entityId,
        entityName: 'PIM',
        properties: getPropertiesParams(payload),
      };

      await unmatchedRequirementMutation.mutateAsync(requestParams, {
        onSuccess: (response) => {
          if (response?.isSuccess) {
            // Only invalidate on final save (Active status)
            if (status === 'Active') {
              queryClient.invalidateQueries({
                queryKey: productFullViewKeys.itemData(entityId),
              });
              invalidateMarketingAssets();
              dispatch(
                productFullViewActions.getProductImagery({
                  hierarchyId: productHierarchyInfo?.productHierarchyId,
                  productId: entityId,
                })
              );
              onMakeActiveSuccessCallback();
            }
          }
        },
      });
    } catch (error) {
      console.error('Error updating product:', error);
    }
  };

  const handleSubmit = async () => {
    try {
      await requiredForm.validateFields();

      // First save without triggering full refetch
      await handleUpdateUnmatchedRequirement();

      // Validate using the validation endpoint, not full product details
      const freshValidateProducts = await onRefetchAndGetValidateProducts();

      const product = freshValidateProducts?.[0];
      const hasErrors = product && !checkPassedRequirementProduct(product);

      if (hasErrors) {
        CustomNotification.warning(
          'Please complete all required fields before activating'
        );
        return;
      }

      // Final save with Active status - this triggers the full refetch
      await handleUpdateUnmatchedRequirement({
        status: 'Active',
      });
    } catch (error) {
      if (error?.errorFields) {
        CustomNotification.warning('Please fill in all required fields');
        return;
      }
      console.error('Error updating product:', error);
    }
  };

  const modalProps = {
    title: 'Update Required Values',
    width: '85%',
    closable: true,
    maskClosable: false,
    bodyStyle: {
      height: '1200px',
    },
    onCancel,
    wrapClassName: 'required-section__modal',
    ...otherProps,
  };

  const isLoading =
    unmatchedRequirementMutation.isLoading || mutateMakeProductActive.isLoading;

  return (
    <StyledModal
      {...modalProps}
      footer={
        <>
          <Space>
            <FormButton
              text='Cancel'
              type='secondary'
              icon={null}
              onClick={onCancel}
            />
            <FormSaveButton
              text='Submit'
              onClick={handleSubmit}
              loading={isLoading}
            />
          </Space>
        </>
      }
    >
      <WithLoading loading={isLoading} tip='Validating...'>
        {children}
      </WithLoading>
    </StyledModal>
  );
};

const RequiredActivePrivateProductSection = ({
  entityId,
  productForm,
  validateProducts,
  productImagery,
  validationTimestamp,
  onRevalidateProduct,
}) => {
  const { requiredForm } = useRequiredActiveProductContext();

  const { allActiveRequirements: flatRequiredProductFields } =
    useGetRequirementsForActiveProduct();

  const additionalFields = Object.keys(additionalRequirementFieldsLowercase);

  const requiredErrorFields = useMemo(() => {
    return getErrorsForActiveProduct(validateProducts?.[0]?.activeResult);
  }, [validateProducts]);

  const primaryErrors = useMemo(
    () => validateProducts?.[0]?.activeResult?.errors ?? [],
    [validateProducts]
  );

  const additionalRequirements = useMemo(
    () => validateProducts?.[0]?.activeResult?.additionalRequirements ?? {},
    [validateProducts]
  );

  const productData = useMemo(() => validateProducts?.[0], [validateProducts]);

  useSetFormValuesAndErrors({
    form: requiredForm,
    productData,
    primaryErrors,
    productForm,
  });

  const filteredEntityRequirement = useMemo(() => {
    let result = [];

    if (
      requiredErrorFields.length > 0 &&
      Object.keys(flatRequiredProductFields)?.length > 0
    ) {
      requiredErrorFields.forEach((requiredItem) => {
        const foundProperty =
          flatRequiredProductFields[`${requiredItem?.toLowerCase()}`];
        foundProperty && result.push(foundProperty);
      });
    }

    return result;
  }, [flatRequiredProductFields, requiredErrorFields]);

  const groupedRequiredProperties = useMemo(() => {
    if (filteredEntityRequirement.length === 0) return [];

    return groupPropertiesByModuleName(filteredEntityRequirement);
  }, [filteredEntityRequirement]);

  const additionalRequiredErrorFields = requiredErrorFields.filter((field) =>
    additionalFields.includes(field?.toLowerCase())
  );

  return (
    <Row style={{ width: '100%' }} gutter={[0, 6]} className='required-section'>
      <RequiredPropertiesForm form={requiredForm} style={{ width: '100%' }}>
        {groupedRequiredProperties?.length > 0 && (
          <RequiredPropertiesSection header='Required Properties'>
            {groupedRequiredProperties.map((groupItem) => {
              return (
                <Col span={24} key={groupItem.moduleDisplayName}>
                  <RequiredModuleName
                    moduleName={groupItem.moduleDisplayName}
                  />
                  {groupItem.moduleProperties.map((property) => {
                    const isError = requiredErrorFields.includes(
                      property?.fieldFullPath?.toLowerCase()
                    );

                    return (
                      <RequiredPropertyItem
                        key={property.fullPathDisplayName}
                        wrap={false}
                      >
                        <RequiredPropertyName
                          propertyName={property.fullPathDisplayName}
                          isError={isError}
                        />

                        {isError && (
                          <RequiredPropertyInput
                            property={property}
                            status='error'
                            primaryErrors={primaryErrors}
                            productImagery={productImagery}
                            mode='edit'
                            entityId={entityId}
                            validationTimestamp={validationTimestamp}
                          />
                        )}
                      </RequiredPropertyItem>
                    );
                  })}
                </Col>
              );
            })}
          </RequiredPropertiesSection>
        )}
        {additionalRequiredErrorFields?.length > 0 && (
          <RequiredPropertiesSection header='Additional Properties'>
            <AdditionalRequiredActiveProduct
              requiredErrorFields={additionalRequiredErrorFields}
              entityId={entityId}
              productForm={productForm}
              onRevalidateProduct={onRevalidateProduct}
              additionalRequirements={additionalRequirements}
            />
          </RequiredPropertiesSection>
        )}
      </RequiredPropertiesForm>
    </Row>
  );
};

export const dialogConfirmForCreateActiveProduct = ({
  onOk = () => {},
  onCancel = () => {},
} = {}) => {
  return dialogFunction({
    type: 'warn',
    title: (
      <Typography.Title level={4} style={{ fontSize: 20 }}>
        Product Activation Requirements
      </Typography.Title>
    ),
    width: 560,
    wrapClassName: 'create-product-form__dialog-confirm',
    content: (
      <div>
        <Paragraph style={{ marginBottom: 8 }}>
          This product is missing required fields and has been saved as{' '}
          <Text strong>Pending</Text>.
        </Paragraph>
        <Paragraph style={{ marginBottom: 0 }}>
          Would you like to complete the required fields now?
        </Paragraph>
      </div>
    ),
    okText: 'Complete Now',
    cancelText: 'Later',
    onOk,
    onCancel,
  });
};
